import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Paper,
  Typography,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Table,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getToken } from "../../../api/Account";
import { DataContext } from "../../../context/DataContext";

import { isAccess, simpleMessage } from "../../../helpers/Helpers";
import { formatearMonto } from "../../../helpers/formato";

import {
  CargandoTabla,
  ConfirmDialog,
  EstadoError,
  EstadoVacio,
  useAviso,
} from "../../../components/base";

import {
  deleteGarantiaAsync,
  getCreditGarantiasAsync,
} from "../../../api/Credits";
import DialogoGarantia from "./garantiaComponents/DialogoGarantia";
import { celdaMonto, encabezadoTabla } from "../comunes";

// Lo que devuelve `errorResponse` cuando el servidor dice que la sesión sigue abierta en otro equipo.
const ERROR_OTRA_SESION = "Sesión activa en otro dispositivo";

const FILAS_POR_PAGINA = 10;

const CreditGarantias = ({ id }) => {
  const token = getToken();
  const [garantiaList, setGarantiaList] = useState([]);
  const [selectedGarantia, setSelectedGarantia] = useState([]);

  const [localReload, setLocalReload] = useState(false);

  const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
  const aviso = useAviso();

  const [addGarantiaModal, setAddGarantiaModal] = useState(false);
  const [editGarantiaModal, setEditGarantiaModal] = useState(false);

  // Estado local de la pestaña: esqueleto mientras carga y error con "Reintentar".
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  // Confirmación de eliminar. Se conserva la fila al cerrar para que el texto no cambie
  // durante la animación de salida.
  const [confirmacion, setConfirmacion] = useState({ abierto: false, fila: null });
  const [procesando, setProcesando] = useState(false);

  const [pagina, setPagina] = useState(0);

  const cargar = async () => {
    setCargando(true);
    setError(false);
    setIsLoading(true);
    try {
      const result = await getCreditGarantiasAsync(token, id);
      if (!result.statusResponse) {
        setIsLoading(false);
        setCargando(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        setError(true);
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
        }
        return;
      }
      setIsLoading(false);
      setGarantiaList(result.data.result);
      setCargando(false);
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      setIsLoading(false);
      setCargando(false);
      setError("conexion");
    }
  };

  useEffect(() => {
    cargar();
  }, [localReload]);

  // Si al eliminar la página actual queda fuera de rango, se vuelve a la primera.
  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= garantiaList.length) {
      setPagina(0);
    }
  }, [pagina, garantiaList.length]);

  const currentItem = garantiaList.slice(
    pagina * FILAS_POR_PAGINA,
    (pagina + 1) * FILAS_POR_PAGINA
  );

  const deleteGarantia = async (item) => {
    setIsLoading(true);
    try {
      const result = await deleteGarantiaAsync(token, item.id);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }
      setIsLoading(false);
      setLocalReload(!localReload);
      aviso.exito("Garantía eliminada");
    } catch (e) {
      setIsLoading(false);
      aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
    }
  };

  const pedirEliminar = (fila) => setConfirmacion({ abierto: true, fila });
  const cerrarConfirmacion = () =>
    setConfirmacion((actual) => ({ ...actual, abierto: false }));

  // Solo aquí se llama a la función de verdad, y una sola vez por confirmación.
  const confirmar = async () => {
    setProcesando(true);
    try {
      await deleteGarantia(confirmacion.fila);
    } finally {
      setProcesando(false);
      cerrarConfirmacion();
    }
  };

  const puedeAgregar = isAccess(access, "CREDITS CREATE");
  const puedeEditar = isAccess(access, "CREDITS UPDATE");
  const puedeEliminar = isAccess(access, "CREDITS DELETE");
  const filaEliminar = confirmacion.fila;

  const botonAgregar = (
    <Button
      onClick={() => {
        setAddGarantiaModal(true);
      }}
      variant="contained"
      startIcon={<AddIcon />}
    >
      Agregar garantía
    </Button>
  );

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={4} columnas={4} etiqueta="Cargando las garantías…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar las garantías"
        }
        onReintentar={cargar}
      />
    );
  } else if (garantiaList.length === 0) {
    contenido = (
      <EstadoVacio
        titulo="Sin garantías"
        descripcion="Este crédito todavía no tiene garantías registradas."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label="Garantías del crédito" size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={encabezadoTabla}>#</TableCell>
                <TableCell sx={encabezadoTabla}>Artículo</TableCell>
                <TableCell sx={encabezadoTabla}>Color</TableCell>
                <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                  Valor estimado
                </TableCell>
                {puedeEditar || puedeEliminar ? (
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                    Acciones
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItem.map((row) => {
                return (
                  <TableRow key={row.id} hover>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {row.id}
                    </TableCell>
                    <TableCell>{row.articulo}</TableCell>
                    <TableCell>{row.color}</TableCell>
                    <TableCell sx={celdaMonto}>{formatearMonto(row.valorEstimado)}</TableCell>
                    {puedeEditar || puedeEliminar ? (
                      <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {puedeEditar ? (
                          <Tooltip title="Editar garantía">
                            <IconButton
                              aria-label={`Editar la garantía #${row.id}`}
                              sx={{ color: "primary.main" }}
                              onClick={() => {
                                setSelectedGarantia(row);
                                setEditGarantiaModal(true);
                              }}
                            >
                              <EditOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                        {puedeEliminar ? (
                          <Tooltip title="Eliminar garantía">
                            <IconButton
                              aria-label={`Eliminar la garantía #${row.id}`}
                              sx={{ color: "error.main" }}
                              onClick={() => pedirEliminar(row)}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {garantiaList.length > FILAS_POR_PAGINA ? (
          <TablePagination
            component="div"
            count={garantiaList.length}
            page={pagina}
            onPageChange={(e, nueva) => setPagina(nueva)}
            rowsPerPage={FILAS_POR_PAGINA}
            rowsPerPageOptions={[]}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            getItemAriaLabel={(tipo) =>
              ({
                first: "Primera página",
                last: "Última página",
                next: "Página siguiente",
                previous: "Página anterior",
              }[tipo])
            }
          />
        ) : null}
      </>
    );
  }

  return (
    <div>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent={"space-between"}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Typography
            component="h2"
            variant="h6"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            Garantías del crédito
          </Typography>
          {puedeAgregar ? botonAgregar : <></>}
        </Stack>

        {contenido}
      </Paper>

      <ConfirmDialog
        abierto={confirmacion.abierto}
        peligro
        cargando={procesando}
        titulo="Eliminar garantía"
        mensaje={
          filaEliminar ? (
            <>
              <Typography component="p" sx={{ mb: 1 }}>
                {`Se eliminará la garantía #${filaEliminar.id}${filaEliminar.articulo ? ` (${filaEliminar.articulo})` : ""}, valorada en ${formatearMonto(filaEliminar.valorEstimado)}.`}
              </Typography>
              <Typography component="p" sx={{ color: "text.secondary" }}>
                Esta acción no se puede deshacer.
              </Typography>
            </>
          ) : null
        }
        textoConfirmar="Eliminar"
        onConfirmar={confirmar}
        onCancelar={cerrarConfirmacion}
      />

      {addGarantiaModal || editGarantiaModal ? (
        <DialogoGarantia
          creditId={id}
          garantia={editGarantiaModal ? selectedGarantia : null}
          onCerrar={() => {
            setAddGarantiaModal(false);
            setEditGarantiaModal(false);
          }}
          onGuardado={(mensaje) => {
            setAddGarantiaModal(false);
            setEditGarantiaModal(false);
            setLocalReload(!localReload);
            aviso.exito(mensaje);
          }}
        />
      ) : null}
    </div>
  );
};

export default CreditGarantias;
